<template>
  <div>
    <!-- Template information -->
    <collapsible-section title="Template information" icon="tim-icons icon-badge">
      <div class="row">
        <div class="col-md-6 col-lg-6 col-sm-12 col-12">
          <base-input label="Template name"
                      type="text"
                      placeholder="e.g., Main booking site"
                      v-model="selectedBss.name"
                      :error="errs.name">
          </base-input>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6 col-lg-6 col-sm-12 col-12">
          <label>Description (optional)</label>
          <el-input
            v-model="selectedBss.description"
            :rows="2"
            type="textarea"
            placeholder="Brief description of this booking page template"
          />
        </div>
      </div>

      <div class="row mt-3">
        <div class="col-md-6 col-lg-4 col-sm-12 col-12">
          <base-input label="URL slug"
                      type="text"
                      :placeholder="selectedBss.is_default ? 'Not required for main template' : 'e.g., private-events'"
                      v-model="selectedBss.slug"
                      :disabled="selectedBss.is_default"
                      :error="errs.slug"
                      @blur="$emit('validate-slug')">
          </base-input>
        </div>
        <div class="col-md-6 col-lg-4 col-sm-12 col-12 d-flex align-items-center" style="padding-top: 1.5rem;">
          <el-switch active-color="#e14eca"
                     v-model="selectedBss.is_default"
                     :disabled="selectedBss.is_default && !isCreateMode"
                     @change="$emit('handle-set-default', $event)" />
          <label class="ml-2 mb-0">Main template</label>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <small class="url-hint" v-if="selectedBss.is_default">
            <i class="tim-icons icon-link-72 mr-1"></i>
            URL: {{ bookingBaseUrl }}{{ group.code }}
          </small>
          <small class="url-hint" v-else-if="selectedBss.slug">
            <i class="tim-icons icon-link-72 mr-1"></i>
            URL: {{ bookingBaseUrl }}{{ group.code }}/{{ selectedBss.slug }}
          </small>
          <small class="url-hint" v-else>
            <i class="tim-icons icon-link-72 mr-1"></i>
            Enter a slug to see the booking page URL
          </small>
        </div>
      </div>
    </collapsible-section>

    <!-- Embed code -->
    <collapsible-section title="Embed code" icon="el-icon-document-copy" :default-open="false">
      <p>Copy the following snippet and embed it in your code to display bookings on your website.
        Click <a target="_blank" href="https://codepen.io/omnisqrl/pen/YPKgrwd">here</a> for live example.</p>
      <div class="embed-code-block mt-2">
        <div class="embed-code-header">
          <div class="embed-code-lang">
            <span class="lang-dot"></span>
            <span>HTML</span>
          </div>
          <button class="embed-copy-btn" @click="$emit('copy', embeddedText)">
            <i class="tim-icons icon-single-copy-04"></i>
            <span>Copy</span>
          </button>
        </div>
        <div class="embed-code-body">
          <pre class="embed-code-content"><code>{{ embeddedText }}</code></pre>
        </div>
      </div>
    </collapsible-section>

    <div class="row mt-3">
      <div class="col">
        <base-button type="primary" v-on:click="$emit('save')">Save changes</base-button>
      </div>
    </div>
  </div>
</template>

<script>
import {Switch} from 'element-ui';
import CollapsibleSection from '../shared/CollapsibleSection.vue';

export default {
  name: 'PublishTab',
  components: {
    CollapsibleSection,
    [Switch.name]: Switch,
  },
  props: {
    selectedBss: {
      type: [Object, String],
      required: true
    },
    group: {
      type: Object,
      default: () => ({})
    },
    errs: {
      type: Object,
      default: () => ({})
    },
    isCreateMode: {
      type: Boolean,
      default: false
    },
    bookingBaseUrl: {
      type: String,
      default: 'https://offthecouch.io/book/'
    },
    embeddedText: {
      type: String,
      default: ''
    }
  }
}
</script>

<style scoped>
.embed-code-block {
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  overflow: hidden;
}
.embed-code-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.25);
}
.embed-code-lang {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.lang-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e34c26;
}
.embed-copy-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.embed-copy-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
  border-color: rgba(255, 255, 255, 0.2);
}
.embed-code-body {
  max-height: 320px;
  overflow: auto;
  background: rgba(0, 0, 0, 0.4);
}
.embed-code-body::-webkit-scrollbar {
  width: 6px;
}
.embed-code-body::-webkit-scrollbar-track {
  background: transparent;
}
.embed-code-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 3px;
}
.embed-code-body::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.25);
}
.embed-code-content {
  margin: 0;
  padding: 16px;
  white-space: pre-wrap;
  word-break: break-all;
  line-height: 1.6;
}
.embed-code-content code {
  color: #42b883;
  font-family: 'SF Mono', 'Fira Code', 'Fira Mono', Menlo, Consolas, monospace;
  font-size: 12px;
}
.url-hint {
  color: #fff;
}
</style>

<style>
/* Light mode overrides - PublishTab */
.white-content .embed-code-block {
  border-color: rgba(0, 0, 0, 0.1);
}
.white-content .embed-code-header {
  background: #f0f1f5;
  border-bottom-color: rgba(0, 0, 0, 0.08);
}
.white-content .embed-code-lang {
  color: rgba(0, 0, 0, 0.5);
}
.white-content .embed-copy-btn {
  border-color: rgba(0, 0, 0, 0.12);
  color: rgba(0, 0, 0, 0.5);
}
.white-content .embed-copy-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  color: rgba(0, 0, 0, 0.8);
  border-color: rgba(0, 0, 0, 0.2);
}
.white-content .embed-code-body {
  background: #1e1e2f;
}
.white-content .url-hint {
  color: #344675;
}
</style>
